import { createFileRoute, Link } from '@tanstack/react-router';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { useJournalEntries, useJournalEntry, useApproveJournalEntry } from '../../../../features/accounting/client';
import type { JournalLine } from '../../../../features/accounting/schemas';
import { useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter } from '../../../../components/ui/Drawer';
import { Button } from '../../../../components/ui/Button';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { Money } from '../../../../components/ui/Money';

export const Route = createFileRoute('/_auth/accounting/journal/')({
  component: JournalQueuePage,
});

function JournalQueuePage() {
  const { data: entries, isLoading, isError, refetch } = useJournalEntries();
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Posted Journal Entries"
        subtitle="Read-only ledger audit trail of all posted journal entries, regardless of source."
        action={
          <Link to="/accounting/general-transactions/new" className="bg-accent-500 text-white px-4 py-2 rounded">
            New Transaction
          </Link>
        }
      />
      <div className="flex-1 w-full min-h-0">
        {(() => {
          if (isLoading) return <Skeleton rows={5} />;
          if (isError) return (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load journal entries</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          );
          return (
            <div className="bg-surface-0 border border-surface-2 rounded-lg overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-surface-1 border-b border-surface-2">
                  <tr>
                    <th className="py-3 px-4 font-medium text-sm">Entry Number</th>
                    <th className="py-3 px-4 font-medium text-sm">Date</th>
                    <th className="py-3 px-4 font-medium text-sm">Description</th>
                    <th className="py-3 px-4 font-medium text-sm">Status</th>
                    <th className="py-3 px-4 font-medium text-sm">Amount</th>
                    <th className="py-3 px-4 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-text-muted">
                        No journal entries found
                      </td>
                    </tr>
                  ) : (
                    entries?.map((entry) => (
                      <tr key={entry.id} className="border-b border-surface-2 last:border-0 hover:bg-surface-50">
                        <td className="py-3 px-4 font-medium font-mono">{entry.entryNumber}</td>
                        <td className="py-3 px-4">{(entry.entryDate || entry.date) ? new Date(entry.entryDate || entry.date!).toLocaleDateString() : '-'}</td>
                        <td className="py-3 px-4 truncate max-w-xs">{entry.description || entry.narration}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            entry.status === 'POSTED' || entry.status === 'APPROVED'
                              ? 'bg-success-100 text-success-700'
                              : 'bg-warning-100 text-warning-700'
                          }`}>
                            {entry.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono"><Money paise={entry.totalAmount || 0} /></td>
                        <td className="py-3 px-4">
                          <Button 
                            variant="ghost" size="sm" className="text-accent-600 hover:underline"
                            onClick={() => setSelectedEntryId(entry.id)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
      {selectedEntryId && (
        <JournalDetailDrawer
          entryId={selectedEntryId}
          onClose={() => setSelectedEntryId(null)}
        />
      )}
    </div>
  );
}


function JournalDetailDrawer({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const { data: entry, isLoading, isError } = useJournalEntry(entryId);
  const approveMutation = useApproveJournalEntry(entryId);

  const lines = entry?.lines || [];
  const displayStatus = entry?.status;

  return (
    <Drawer open={!!entryId} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="w-full max-w-xl bg-surface-1">
        <DrawerHeader>
          <DrawerTitle>Journal Entry Detail</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          {isLoading ? (
            <Skeleton rows={5} />
          ) : isError || !entry ? (
            <p className="text-danger-600">Failed to load journal entry details.</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-text-muted block">Entry Number</span>
                  <span className="font-semibold text-text-primary">{entry.entryNumber}</span>
                </div>
                <div>
                  <span className="text-xs text-text-muted block">Date</span>
                  <span className="font-semibold text-text-primary">{(entry.entryDate || entry.date) ? new Date(entry.entryDate || entry.date!).toLocaleDateString() : '-'}</span>
                </div>
                <div>
                  <span className="text-xs text-text-muted block">Status</span>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                    displayStatus === 'POSTED' || displayStatus === 'APPROVED'
                      ? 'bg-success-100 text-success-700'
                      : 'bg-warning-100 text-warning-700'
                  }`}>
                    {displayStatus}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-text-muted block">Source Type</span>
                  <span className="font-semibold text-text-primary">{entry.sourceType}</span>
                </div>
              </div>

              <div>
                <span className="text-xs text-text-muted block mb-1">Description / Narration</span>
                <p className="text-sm bg-surface-2 p-3 rounded">{entry.description || entry.narration || 'No description'}</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold mb-2">Journal Lines</h4>
                <div className="border border-surface-2 rounded-lg overflow-hidden">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-surface-2 text-text-muted">
                      <tr>
                        <th className="py-2 px-3">Account</th>
                        <th className="py-2 px-3 text-right">Debit</th>
                        <th className="py-2 px-3 text-right">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line: JournalLine, idx: number) => {
                        const dr = line.drAmount !== undefined ? line.drAmount : (line.debit || 0);
                        const cr = line.crAmount !== undefined ? line.crAmount : (line.credit || 0);
                        return (
                          <tr key={idx} className="border-b border-surface-2 last:border-0">
                            <td className="py-2 px-3">
                              <div className="font-medium text-text-primary">{line.accountName}</div>
                              <div className="text-xs text-text-muted font-mono">{line.accountCode}</div>
                            </td>
                            <td className="py-2 px-3 text-right font-mono">
                              {dr > 0 ? <Money paise={dr} /> : '-'}
                            </td>
                            <td className="py-2 px-3 text-right font-mono">
                              {cr > 0 ? <Money paise={cr} /> : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-surface-2 font-semibold">
                      <tr>
                        <td className="py-2 px-3">Total</td>
                        <td className="py-2 px-3 text-right font-mono">
                          <Money paise={lines.reduce((sum: number, l: JournalLine) => sum + (l.drAmount !== undefined ? l.drAmount : (l.debit || 0)), 0)} />
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          <Money paise={lines.reduce((sum: number, l: JournalLine) => sum + (l.crAmount !== undefined ? l.crAmount : (l.credit || 0)), 0)} />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {!isLoading && entry && displayStatus === 'DRAFT' && (
            <Button
              variant="primary"
              loading={approveMutation.isPending}
              onClick={async () => {
                await approveMutation.mutateAsync();
                onClose();
              }}
            >
              Approve & Post
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
